import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getVisibleDays, startOfIsoWeek } from "./calendar.utils";
import styles from "./ScheduleCalendar.module.css";

interface MiniCalendarProps {
  selectedDate: Dayjs;
  onSelectDate: (date: Dayjs) => void;
}

export default function MiniCalendar({
  selectedDate,
  onSelectDate,
}: MiniCalendarProps) {
  const { t } = useTranslation();
  const [displayMonth, setDisplayMonth] = useState(selectedDate.startOf("month"));

  useEffect(() => {
    setDisplayMonth(selectedDate.startOf("month"));
  }, [selectedDate]);

  const days = useMemo(() => {
    const start = startOfIsoWeek(displayMonth.startOf("month"));
    return Array.from({ length: 42 }, (_, index) => start.add(index, "day"));
  }, [displayMonth]);
  const weekDays = getVisibleDays(startOfIsoWeek(dayjs()), "week");

  return (
    <section className={styles.miniCalendar} aria-label={t("calendar.mini.title")}>
      <div className={styles.miniCalendarHeader}>
        <strong>{displayMonth.format("MMMM YYYY")}</strong>
        <div>
          <button
            type="button"
            aria-label={t("calendar.mini.previousMonth")}
            onClick={() => setDisplayMonth((value) => value.subtract(1, "month"))}
          >
            ‹
          </button>
          <button
            type="button"
            aria-label={t("calendar.mini.nextMonth")}
            onClick={() => setDisplayMonth((value) => value.add(1, "month"))}
          >
            ›
          </button>
        </div>
      </div>
      <div className={styles.miniCalendarGrid}>
        {weekDays.map((day) => (
          <span key={day.format("dd")} className={styles.miniWeekDay}>
            {day.format("dd").slice(0, 1)}
          </span>
        ))}
        {days.map((day) => {
          const selected = day.isSame(selectedDate, "day");
          const today = day.isSame(dayjs(), "day");
          const outside = day.month() !== displayMonth.month();
          const className = [
            styles.miniDay,
            selected ? styles.miniDaySelected : "",
            today ? styles.miniDayToday : "",
            outside ? styles.miniDayOutside : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <button
              key={day.format("YYYY-MM-DD")}
              type="button"
              className={className}
              aria-label={day.format("D MMMM YYYY")}
              aria-current={today ? "date" : undefined}
              onClick={() => onSelectDate(day)}
            >
              {day.date()}
            </button>
          );
        })}
      </div>
    </section>
  );
}
